/** @jsx jsx */
import type { FC, ChangeEvent, FocusEventHandler } from 'react';
import React, { memo, useEffect, useCallback, useState, useRef } from 'react';
// oxlint-disable-next-line no-unused-vars -- the JSX pragma is used in this file, but the jsx import itself is not referenced directly
import type { StyledProps } from '@compiled/react';
import { styled, jsx, css } from '@compiled/react';
import { defineMessages, useIntl } from 'react-intl';

import { token } from '@atlaskit/tokens';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import Box from '@atlaskit/primitives/box';
import Text from '@atlaskit/primitives/text';
import { xcss } from '@atlaskit/primitives/xcss';
import { Inline } from '@atlaskit/primitives/compiled/inline';
import { cssMap, cx } from '@atlaskit/css';
import { Checkbox } from '@atlaskit/checkbox/checkbox';
import FadeIn from '@atlaskit/motion/fade-in';
import Lozenge from '@atlaskit/lozenge/lozenge';
import Tooltip from '@atlaskit/tooltip/Tooltip';
import PlanIcon from '@atlaskit/icon-lab/core/plan';

import { LazyEmojiComponentLoader } from '@confluence/emoji-title/entry-points/EmojiComponent.tsx';
import { CONTEXT_PATH } from '@confluence/named-routes/entry-points/context.ts';
import type { RenderItemParams } from '@confluence/tree/entry-points/tree-types.ts';
import { ContentTreeIconLoader as ContentTreeIcon } from '@confluence/icons/entry-points/ContentTreeIcon.ts';
import { ManagedFolderClosedIcon } from '@confluence/smart-folder/entry-points/ManagedFolderClosedIcon.ts';
import { ManagedFolderOpenIcon } from '@confluence/smart-folder/entry-points/ManagedFolderOpenIcon.ts';
import { includesValidContentTreeType } from '@confluence/icons/entry-points/contentTreeTypes.ts';
import type { ContentTreeTypes } from '@confluence/icons/entry-points/contentTreeTypes.ts';
import { getURLBypassingResumeDraftAction } from '@confluence/content-utils/entry-points/resumeDraftActionBypass.ts';
import { removeContentSlugFromUrl } from '@confluence/content-utils/entry-points/removeContentSlugFromUrl.ts';
import { PAGE_TREE_ITEM_CLICK } from '@confluence/browser-metrics/entry-points/viewPageTransitionSourceConstants.ts';
import { setViewTransitionSource } from '@confluence/browser-metrics/entry-points/viewTransitionSource.ts';
import { fg } from '@confluence/feature-gating';
import { expValEquals } from '@confluence/feature-experiments';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { useOptimisticTitleChange } from '@confluence/page-tree-refresh-state-container/entry-points/useOptimisticTitleChange.ts';
import { perfMarkEnd } from '@confluence/performance/entry-points/performance.ts';
import { LivePagePreloader } from '@confluence/live-page-preloader/entry-points/LivePagePreloader.ts';
import { HoverPreloader, PreloadingPresets } from '@confluence/hover-preloader';
import { useIsMultiSelectActive } from '@confluence/content-tree-actions/entry-points/useIsMultiSelectActive.ts';
import { useIsMarkdownMode } from '@confluence/content-markdown-mode/entry-points/useIsMarkdownMode.ts';
import { useContentTreeSelection } from '@confluence/content-tree-actions/entry-points/store.ts';
import { withFlags } from '@confluence/flags/entry-points/withFlags.ts';
import { type FlagsStateContainer } from '@confluence/flags/entry-points/FlagsStateContainer.ts';

import { ANIMATE_TIME_SEC } from '../entry-points/PageTreeLoader.tsx';

import type { PageTreeInteractionSource } from './analytics.ts';
import type { ContentTreeItem } from './data-transformers.ts';
import { PageTreeAfterIconLoader as PageTreeAfterIcon } from './PageTreeAfterIconLoader.tsx';
import { ConditionalTreeItemTooltip } from './ConditionalTreeItemTooltip.tsx';
import { ConditionalInlineRename } from './quick-actions/ConditionalInlineRename.tsx';
import { HomepageIcon } from './HomepageIconLoader.ts';
import { DraftLozenge } from './DraftLozengeLoader.ts';
import { PageTreeLinkItem } from './PageTreeLinkItem.tsx';
import { usePreloadOnDelayedHover } from './preloading/usePreloadOnDelayedHover.ts';
import { useCollabDraftPrefetch } from './preloading/useCollabDraftPrefetch.ts';
import { useNestedSelect } from './bulk-actions/useNestedSelect.ts';
import { useNestedDeselect } from './bulk-actions/useNestedDeselect.ts';
import { BeforeIcon } from './PageTreeBeforeIcon.tsx';
import { SmartFolderSyncSpinner } from './smart-folders/SmartFolderSyncSpinner.tsx';

// Checks if the content type supports emojis and if an emoji is set
export const useIsEmojiSetForType = (contentType: string | undefined, emoji: any): boolean => {
	// Add other checks for other content types and corresponding feature flags here as they get implemented.
	if (contentType === 'whiteboard') {
		return !!emoji && fg('confluence_frontend_whiteboard_emoji_titles');
	}

	if (contentType === 'page') {
		return !!emoji;
	}

	return false;
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const TitleWrapper = styled.span({
	display: 'inline-block',
	overflow: 'hidden',
	whiteSpace: 'nowrap',
	textOverflow: 'ellipsis',
});

const contentIconWrapperStyles = xcss({
	display: 'flex',
	justifyContent: 'center',
	alignItems: 'center',
	marginRight: 'space.050',
	marginLeft: 'space.025',
	width: '24px',
	height: '24px',
	flexShrink: 0,
});

/**
 * 1. Emojis are rendered as images, and img has draggable=true set by default, which interferes with the DraggableTreeItem.
 * 2. Also, the Emoji component calls preventDefault on crucial events like onMouseDown, which also breaks pragmatic-dnd.
 * Since we don't need emojis to be interactive for this use case, we can just disable pointer-events on them entirely.
 */
// eslint-disable-next-line @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const EmojiWrapper: React.ComponentType<
	React.ClassAttributes<HTMLSpanElement> & React.HTMLAttributes<HTMLSpanElement> & StyledProps
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled
> = styled.span({
	pointerEvents: 'none',
	display: 'flex',
	flexShrink: 0,
	alignItems: 'center',
	marginRight: token('space.100'),
	marginLeft: token('space.075'),
	height: '16px',
	width: '16px',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'img, svg': {
		height: '16px',
		width: '16px',
	},
});

const emojiWrapperStyles = css({
	display: 'flex',
	justifyContent: 'center',
	alignItems: 'center',
	marginInlineStart: token('space.075'),
	marginInlineEnd: token('space.100'),
	marginBlock: token('space.050'),
	height: '16px',
	width: '16px',
	overflow: 'hidden',
	flexShrink: 0,
});

export const emojiWrapper: FC<{
	hasCoverPicture?: boolean;
	isPageTitleComponent?: boolean;
	shouldRemoveEmojiLeftPadding?: boolean;
}> = ({
	hasCoverPicture: _hasCoverPicture,
	isPageTitleComponent: _isPageTitleComponent,
	shouldRemoveEmojiLeftPadding: _shouldRemoveEmojiLeftPadding,
	...props
}) => {
	return <span css={emojiWrapperStyles} {...props} />;
};

// Wrapper around the after-icon area. We need align-items: center here so our direct
// children (sync spinner + AfterIconContainer) sit on the title baseline. The
// AfterIconContainer itself sets alignItems: center — see PageTreeAfterIcon.tsx.
const afterIconRowStyles = cssMap({
	root: {
		height: '24px',
	},
});

export const PageTreeItemContent: FC<{
	id: string;
	displayHomePageIcon: boolean;
	displayEmoji: any;
	displayTitle: string;
	isDraft: boolean;
	renderResourcedEmoji?: boolean;
	contentType?: string;
	isSelected?: boolean;
	draggableState?: string;
	emoji?: React.ReactNode;
	isExpanded?: boolean;
	isLive?: boolean;
	isManaged?: boolean;
	isDisabled?: boolean;
	isJiraPlannerFolder?: boolean;
}> = memo(
	({
		id,
		displayHomePageIcon,
		displayEmoji,
		displayTitle,
		isDraft,
		renderResourcedEmoji,
		contentType,
		isSelected,
		draggableState,
		emoji,
		isExpanded,
		isLive,
		isManaged,
		isDisabled,
		isJiraPlannerFolder,
	}) => {
		const iconColor = isSelected ? token('color.icon.selected') : undefined;
		const disabledIconColor = token('color.icon.disabled');
		const isDragging = draggableState === 'dragging';
		const isEmojiSet = useIsEmojiSetForType(contentType, emoji);
		const isContentTreeIconVisible =
			contentType && includesValidContentTreeType(contentType) && !isEmojiSet;
		const isEmojiOptimized = !!emoji;
		const isEmbedWithoutEmoji = contentType === 'embed' && !displayEmoji;
		const iconPrimaryColor = isDragging || isDisabled ? disabledIconColor : iconColor;
		const isLiveDoc = contentType === 'page' && isLive;
		const isManagedFolder = contentType === 'folder' && isManaged;
		const isMarkdownMode = useIsMarkdownMode(isLiveDoc ? id : undefined);

		return (
			<Inline as="span" alignBlock="center">
				{displayHomePageIcon ? <HomepageIcon /> : null}
				{isContentTreeIconVisible && (
					<Box as="span" xcss={contentIconWrapperStyles}>
						{isJiraPlannerFolder ? (
							<PlanIcon label="" size="medium" color={iconPrimaryColor} />
						) : isManagedFolder ? (
							isExpanded ? (
								<ManagedFolderOpenIcon label="" size="small" color={iconPrimaryColor} />
							) : (
								<ManagedFolderClosedIcon label="" size="small" color={iconPrimaryColor} />
							)
						) : (
							<ContentTreeIcon
								type={
									isLiveDoc && fg('confluence_live_pages_open_beta_trait_opted_in')
										? 'liveDoc'
										: (contentType as ContentTreeTypes)
								}
								label=""
								color={iconPrimaryColor}
								isExpanded={isExpanded}
								isMarkdownMode={isMarkdownMode}
							/>
						)}
					</Box>
				)}
				{(displayEmoji || isEmbedWithoutEmoji) &&
					(isEmojiOptimized ? (
						emoji
					) : (
						<LazyEmojiComponentLoader
							key={displayEmoji}
							emoji={displayEmoji}
							contentType={contentType}
							context="pageTree"
							height={14}
							primaryColor={iconPrimaryColor}
							wrapper={EmojiWrapper}
							renderResourcedEmoji={renderResourcedEmoji}
						/>
					))}
				{/* eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766 */}
				<TitleWrapper id={id} className="title">
					<Text weight="medium" color={getTitleTextColor(isDragging, isSelected)}>
						{isLiveDoc && isMarkdownMode && displayTitle ? `${displayTitle}.md` : displayTitle}
					</Text>
				</TitleWrapper>
				{isDraft && <DraftLozenge />}
			</Inline>
		);
	},
);

const getTitleTextColor = (isDragging, isSelected) => {
	if (isDragging) {
		return 'color.text.disabled';
	} else if (isSelected) {
		return 'color.text.selected';
	}
	return 'color.text.subtle';
};

export type PageTreeItemProps = {
	item: ContentTreeItem;
	draftText: string;
	spaceHomePageId?: string;
	onHighlight(): void;
	isSuperAdmin: boolean;
	onClick(event: any): void;
	isQuickActionsEnabled: boolean;
	isInlineRenameEnabled: boolean;
	isDraft: boolean;
	spaceKey: string;
	emoji?: React.ReactNode;
	isHoverPageCardOptedIn: boolean;
	closeAllHoverPageCards: () => void;
	isQuickActionsOpen: boolean;
	setOpenQuickActionsId: (id: string | null) => void;
	isQuickActionsFocused: boolean;
	setFocusedQuickActionsId: (focusedQuickActionsId: string | null) => void;
	editingTitle: { contentId: string | null; isNewContent?: boolean } | null;
	setIsEditingTitleId: (isEditingTitleId: string | null) => Promise<void>;
	onEnterHoverTarget: (params: {
		itemID: string;
		isQuickActionsFocused: boolean;
		hoverPageCardTriggerRef: React.RefObject<HTMLDivElement>;
		isContextualCreateFocused: boolean;
	}) => void;
	onLeaveHoverTarget: () => void;
	onBlurHoverTarget: (e: any) => void;
	updateSinglePage: (id: string, data: object) => void;
	shouldRenderAfterIcon?: boolean;
	contentStatus?: string;
	renderResourcedEmoji?: boolean;
	hideDraftHrefs?: boolean;
	flags: FlagsStateContainer;
	renderServerPlaceholder: boolean;
	onInteractionSourceChange?: (
		itemId: string,
		interactionSource: PageTreeInteractionSource,
	) => void;
	/**
	 * Whether smart folders are enabled for this space. Provided by the parent
	 * (PageTreeCoordinator), which calls `useIsSmartFoldersEnabled` once for the
	 * entire tree instead of once per row. Defaults to `false` when not provided.
	 */
	isSmartFoldersEnabledForTree?: boolean;
} & RenderItemParams<ContentTreeItem>;

const PageTreeItemComponent: FC<PageTreeItemProps> = ({
	item,
	draftText = '',
	spaceHomePageId,
	onExpand,
	onCollapse,
	onInteractionSourceChange,
	onHighlight,
	draggableState,
	isSuperAdmin,
	onClick,
	spaceKey,
	indent,
	dragHandleProps,
	isHoverPageCardOptedIn,
	closeAllHoverPageCards,
	isQuickActionsOpen,
	setOpenQuickActionsId,
	isQuickActionsFocused,
	setFocusedQuickActionsId,
	editingTitle,
	setIsEditingTitleId,
	onEnterHoverTarget,
	onLeaveHoverTarget,
	onBlurHoverTarget,
	isDraft,
	updateSinglePage,
	shouldRenderAfterIcon,
	contentStatus,
	isSmartFoldersEnabledForTree,
	renderResourcedEmoji,
	hideDraftHrefs,
	emoji,
	flags,
	renderServerPlaceholder,
}) => {
	const intl = useIntl();
	const hoverPageCardTriggerRef = useRef<HTMLDivElement>(null);
	const displayEmoji = item.data.emoji;
	const [isMultiSelectActive] = useIsMultiSelectActive();
	const [{ selectedContent }] = useContentTreeSelection();

	const isChecked = item.id.toString() in selectedContent;

	const onOptimisticUpdate = useCallback(
		(_, title: string) => {
			updateSinglePage(item.id, {
				title,
				fullTitle: title,
			});
		},
		[updateSinglePage, item.id],
	);

	const defaultTitle = item.data.title || draftText;
	const { optimisticTitle } = useOptimisticTitleChange(item.id, defaultTitle, onOptimisticUpdate);
	const displayTitle = optimisticTitle || draftText;

	const contentType = item.data.type;
	const isHighlighted = item.data.isHighlighted;
	const selected = item.data.isSelected;
	const isLive = !!(item?.data?.subType === 'live');
	const isManaged = item?.data?.subType === 'managed';

	// isSmartFoldersEnabledForTree is hoisted from PageTreeCoordinatorComponent, which calls
	// useIsSmartFoldersEnabled once for the entire tree instead of once per row.
	const isSmartFoldersEnabled = isSmartFoldersEnabledForTree ?? false;

	const { isHideTitleInUrl } = useSessionData();

	// For drafts, webui can be a link to `resumedraft.action`, so this bypass is used to speed up navigation in those cases.
	const bypassHref =
		isDraft && hideDraftHrefs
			? null
			: getURLBypassingResumeDraftAction({
					url: `${CONTEXT_PATH}${item.data.webui}`,
					spaceKey,
					contentType: item.data.type,
					editorVersion: item.data.editorVersion as any, //TODO: fix this type
					contentId: item.id,
				});

	// When the isHideTitleInUrl setting is on, strip the content title slug from the href URL.
	const itemHref =
		isHideTitleInUrl &&
		bypassHref &&
		expValEquals('confluence_hide_content_title_in_url', 'isEnabled', true)
			? removeContentSlugFromUrl(bypassHref)
			: bypassHref;

	useEffect(() => {
		// if this is a highlighted page, let parent know when it's finished being
		// visually highlighted (on mount + animation duration)
		if (isHighlighted) {
			const timeoutId = setTimeout(onHighlight, ANIMATE_TIME_SEC * 1000);
			return () => clearTimeout(timeoutId);
		}
	}, [isHighlighted, onHighlight]);

	const editingTitleId = editingTitle?.contentId;
	const isEditingTitle = editingTitleId === item.id;
	const editingTitleIsNewContent = editingTitle?.isNewContent;
	useEffect(() => {
		let isNewContent = false;
		if (editingTitleIsNewContent && isEditingTitle) {
			// This case handles new folder creation
			isNewContent = true;
		} else if (selected && item.data.createdDate) {
			// This case handles new non-folder content creation, by assuming that
			// visting content that was created in the last 20 seconds means you just created it.
			const now = new Date();
			const createdDate = new Date(Number(item.data.createdDate));
			if (now.getTime() - createdDate.getTime() < 1000 * 20) {
				isNewContent = true;
			}
		}

		if (isNewContent) {
			perfMarkEnd({
				subject: 'PageTree',
				subjectId: 'newContentRendered',
				details: {
					contentType,
				},
			});
		}
	}, [selected, item.data.createdDate, editingTitleIsNewContent, contentType, isEditingTitle]);

	const isCursor = item.data.isCursor;
	useEffect(() => {
		if (isCursor) {
			perfMarkEnd({
				subject: 'PageTree',
				subjectId: 'loadMorePages',
			});
		}
	}, [isCursor]);

	const [isContextualCreateFocused, setIsContextualCreateFocused] = useState<boolean>(false);

	const {
		onPreloadMouseEnter,
		onPreloadMouseLeave,
		onPreloadMouseDown,
		onPreloadMouseMove,
		isPreloadOnDelayedHoverEnabled,
	} = usePreloadOnDelayedHover({
		itemId: item.id.toString(),
		isLive,
		contentType: contentType || 'page',
		isSelected: Boolean(selected),
	});

	const {
		onPrefetchMouseEnter,
		onPrefetchMouseLeave,
		onPrefetchMouseDown,
		onPrefetchFocus,
		onPrefetchBlur,
	} = useCollabDraftPrefetch({
		itemId: item.id.toString(),
		isLive,
		isDraft,
		contentType: contentType || 'page',
	});

	const isCheckboxDisabled = isDraft || isManaged;

	const pageTreeItemContent = isPreloadOnDelayedHoverEnabled ? (
		<span
			role="presentation"
			onMouseEnter={onPreloadMouseEnter}
			onMouseLeave={onPreloadMouseLeave}
			onFocus={onPrefetchFocus}
			onBlur={onPrefetchBlur}
			onMouseDown={onPreloadMouseDown}
			onMouseMove={onPreloadMouseMove}
		>
			<PageTreeItemContent
				id={item.id.toString()}
				displayHomePageIcon={item.id === spaceHomePageId}
				isDraft={isDraft}
				displayTitle={displayTitle}
				displayEmoji={displayEmoji}
				contentType={contentType}
				isSelected={selected}
				renderResourcedEmoji={renderResourcedEmoji}
				draggableState={draggableState}
				emoji={emoji}
				isExpanded={item.isExpanded}
				isLive={isLive}
				isManaged={isManaged}
				isDisabled={isMultiSelectActive && isCheckboxDisabled}
				isJiraPlannerFolder={item.data.isJiraPlannerFolder}
			/>
		</span>
	) : (
		<PageTreeItemContent
			id={item.id.toString()}
			displayHomePageIcon={item.id === spaceHomePageId}
			isDraft={isDraft}
			displayTitle={displayTitle}
			displayEmoji={displayEmoji}
			contentType={contentType}
			isSelected={selected}
			renderResourcedEmoji={renderResourcedEmoji}
			draggableState={draggableState}
			emoji={emoji}
			isExpanded={item.isExpanded}
			isLive={isLive}
			isManaged={isManaged}
			isDisabled={isMultiSelectActive && isCheckboxDisabled}
			isJiraPlannerFolder={item.data.isJiraPlannerFolder}
		/>
	);
	const selectContentAndDescendants = useNestedSelect();
	const deselectContentAndDescendants = useNestedDeselect();

	const onBlurCallback = useCallback<FocusEventHandler<HTMLDivElement>>(
		(e) => {
			onPrefetchBlur();
			onBlurHoverTarget(e);
		},
		[onBlurHoverTarget, onPrefetchBlur],
	);

	const checkBox = (
		<Checkbox
			isChecked={isChecked && !isDraft}
			onChange={(e: ChangeEvent<HTMLInputElement>) => {
				if (e.target.checked) {
					selectContentAndDescendants(item.id, true, flags);
				} else {
					deselectContentAndDescendants(item.id);
				}
			}}
			name="isPageTreeItemSelected"
			aria-label={intl.formatMessage(i18n.checkboxLabel, {
				contentTitle: item.data.title,
			})}
			isDisabled={isCheckboxDisabled}
		/>
	);
	if (isMultiSelectActive) {
		const selectedItem = selectedContent[item.id];
		const hasDescendantCount = selectedItem && selectedItem.selectedDescendantCount > 0;

		return (
			// contain: 'content' fixes an unexpected white strip appearing next to because of FadeIn and truncated text clash
			// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop
			<div style={{ contain: 'content' }}>
				<Inline alignInline="start" alignBlock="center" space="space.0">
					<FadeIn>
						{(props) => (
							<span {...props}>
								{isDraft ? (
									<Tooltip content={intl.formatMessage(i18n.draftTooltip)}>{checkBox}</Tooltip>
								) : isManaged ? (
									<Tooltip content={intl.formatMessage(i18n.managedTooltip)}>{checkBox}</Tooltip>
								) : (
									checkBox
								)}
							</span>
						)}
					</FadeIn>
					<FadeIn entranceDirection="left">
						{(props) => (
							<span
								{...props}
								// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop
								style={{ ...props.style, flexGrow: 1, minWidth: 0 }}
							>
								<ConditionalTreeItemTooltip
									title={displayTitle}
									isHoverPageCardOptedIn={false}
									isQuickActionsOpen={false}
								>
									<PageTreeLinkItem
										renderAs="span"
										isSelected={Boolean(selected)}
										onClick={(event) => {
											event.preventDefault();
											event.stopPropagation();
											if (!isCheckboxDisabled) {
												if (!isChecked) {
													selectContentAndDescendants(item.id, true, flags);
												} else {
													deselectContentAndDescendants(item.id);
												}
											}
										}}
										href={itemHref}
										item={item}
										isSuperAdmin={isSuperAdmin}
										indent={indent}
										forceHoverBackground={false}
										isQuickActionsFocused={false}
										isDisabled={isCheckboxDisabled}
										beforeIcon={
											<BeforeIcon
												item={item}
												isSuperAdmin={isSuperAdmin}
												onExpand={(event) => {
													event.preventDefault();
													event.stopPropagation();
													onInteractionSourceChange?.(item.id, 'chevron');
													onExpand?.(item.id);
												}}
												onCollapse={(event) => {
													event.preventDefault();
													event.stopPropagation();
													onInteractionSourceChange?.(item.id, 'chevron');
													onCollapse?.(item.id);
												}}
											/>
										}
										afterIcon={
											item.hasChildren && !item.isExpanded && hasDescendantCount ? (
												<Lozenge isBold>+{selectedItem.selectedDescendantCount}</Lozenge>
											) : undefined
										}
									>
										{pageTreeItemContent}
									</PageTreeLinkItem>
								</ConditionalTreeItemTooltip>
							</span>
						)}
					</FadeIn>
				</Inline>
			</div>
		);
	}

	//SSR placeholder
	//this component is a lighter weight version of pageTreeItem. anytime the HTML or CSS of pageTreeITem is updated
	// it's critical that this is also updated.
	// data-ssr-placeholder is handled in renderPageTreeItem on the wrapper for each item
	if (renderServerPlaceholder) {
		return (
			<Box role="presentation">
				<div>
					<div data-contentid={item.id} role="presentation">
						<PageTreeLinkItem
							dragHandleProps={dragHandleProps}
							isSelected={Boolean(selected)}
							href={itemHref}
							item={item}
							draggableState={draggableState}
							isSuperAdmin={isSuperAdmin}
							indent={indent}
							onClick={noop}
							forceHoverBackground={false}
							isQuickActionsFocused={false}
							beforeIcon={
								<BeforeIcon
									item={item}
									isSuperAdmin={isSuperAdmin}
									onCollapse={noop}
									onExpand={noop}
								/>
							}
						>
							{pageTreeItemContent}
						</PageTreeLinkItem>
					</div>
				</div>
			</Box>
		);
	}

	return (
		<LivePagePreloader isLivePage={isLive}>
			<HoverPreloader
				name="page-tree-item"
				preloadUrl={itemHref ?? ''}
				preloadOptions={PreloadingPresets.onlyJS}
			>
				<div
					onMouseEnter={onPrefetchMouseEnter}
					onMouseLeave={onPrefetchMouseLeave}
					onFocus={onPrefetchFocus}
					onBlur={onBlurCallback}
					onMouseDown={() => {
						onPrefetchMouseDown();
						if (contentType === 'page' && !isDraft) {
							setViewTransitionSource(PAGE_TREE_ITEM_CLICK);
						}
						closeAllHoverPageCards();
						isQuickActionsOpen && setOpenQuickActionsId(null);
					}}
					ref={hoverPageCardTriggerRef}
					data-contentid={item.id}
					role="presentation"
				>
					<ConditionalInlineRename
						id={item.id}
						title={item.data.fullTitle || ''}
						displayEmoji={item.data.emojiFromProperties || ''}
						isEditingTitle={isEditingTitle}
						isNewContent={editingTitleIsNewContent}
						setIsEditingTitleId={setIsEditingTitleId}
						setFocusedQuickActionsId={setFocusedQuickActionsId}
						updateContentTreeItem={updateSinglePage}
						contentStatus={contentStatus}
						contentType={contentType}
						isLive={isLive}
						indent={indent}
					>
						<ConditionalTreeItemTooltip
							title={displayTitle}
							isHoverPageCardOptedIn={isHoverPageCardOptedIn}
							isQuickActionsOpen={isQuickActionsOpen}
						>
							<PageTreeLinkItem
								dragHandleProps={dragHandleProps}
								onMouseEnter={() => {
									onEnterHoverTarget({
										itemID: item.id.toString(),
										isQuickActionsFocused,
										hoverPageCardTriggerRef,
										isContextualCreateFocused,
									});
								}}
								onMouseLeave={onLeaveHoverTarget}
								onBlur={onBlurHoverTarget}
								isSelected={Boolean(selected)}
								onClick={onClick}
								href={itemHref}
								item={item}
								draggableState={draggableState}
								isSuperAdmin={isSuperAdmin}
								indent={indent}
								forceHoverBackground={isQuickActionsOpen || isContextualCreateFocused}
								isQuickActionsFocused={isQuickActionsFocused}
								beforeIcon={
									<BeforeIcon
										item={item}
										isSuperAdmin={isSuperAdmin}
										onExpand={(event) => {
											event.preventDefault();
											event.stopPropagation();
											onInteractionSourceChange?.(item.id, 'chevron');
											onExpand?.(item.id);
										}}
										onCollapse={(event) => {
											event.preventDefault();
											event.stopPropagation();
											onInteractionSourceChange?.(item.id, 'chevron');
											onCollapse?.(item.id);
										}}
									/>
								}
								afterIcon={
									isManaged || shouldRenderAfterIcon ? (
										<Inline
											as="span"
											alignBlock="center"
											space="space.050"
											xcss={cx(afterIconRowStyles.root)}
										>
											{isManaged && isSmartFoldersEnabled && (
												<SmartFolderSyncSpinner folderId={item.id} />
											)}
											{shouldRenderAfterIcon && (
												<PageTreeAfterIcon
													setFocusedQuickActionsId={setFocusedQuickActionsId}
													isSuperAdmin={isSuperAdmin}
													isQuickActionsOpen={isQuickActionsOpen}
													setOpenQuickActionsId={setOpenQuickActionsId}
													setIsEditingTitleId={setIsEditingTitleId}
													closeAllHoverPageCards={closeAllHoverPageCards}
													spaceKey={spaceKey}
													item={item}
													isContextualCreateFocused={isContextualCreateFocused}
													setIsContextualCreateFocused={setIsContextualCreateFocused}
													displayTitle={displayTitle}
													contentType={contentType}
												/>
											)}
										</Inline>
									) : null
								}
							>
								{pageTreeItemContent}
							</PageTreeLinkItem>
						</ConditionalTreeItemTooltip>
					</ConditionalInlineRename>
				</div>
			</HoverPreloader>
		</LivePagePreloader>
	);
};

export const PageTreeItem: React.ComponentType<
	Omit<
		{
			item: ContentTreeItem;
			draftText: string;
			spaceHomePageId?: string;
			onHighlight(): void;
			isSuperAdmin: boolean;
			onClick(event: any): void;
			isQuickActionsEnabled: boolean;
			isInlineRenameEnabled: boolean;
			isDraft: boolean;
			spaceKey: string;
			emoji?: React.ReactNode;
			isHoverPageCardOptedIn: boolean;
			closeAllHoverPageCards: () => void;
			isQuickActionsOpen: boolean;
			setOpenQuickActionsId: (id: string | null) => void;
			isQuickActionsFocused: boolean;
			setFocusedQuickActionsId: (focusedQuickActionsId: string | null) => void;
			editingTitle: { contentId: string | null; isNewContent?: boolean } | null;
			setIsEditingTitleId: (isEditingTitleId: string | null) => Promise<void>;
			onEnterHoverTarget: (params: {
				itemID: string;
				isQuickActionsFocused: boolean;
				hoverPageCardTriggerRef: React.RefObject<HTMLDivElement>;
				isContextualCreateFocused: boolean;
			}) => void;
			onLeaveHoverTarget: () => void;
			onBlurHoverTarget: (e: any) => void;
			updateSinglePage: (id: string, data: object) => void;
			shouldRenderAfterIcon?: boolean;
			contentStatus?: string;
			renderResourcedEmoji?: boolean;
			hideDraftHrefs?: boolean;
			flags: FlagsStateContainer;
			renderServerPlaceholder: boolean;
			/**
			 * Whether smart folders are enabled for this space. Provided by the parent
			 * (PageTreeCoordinator), which calls `useIsSmartFoldersEnabled` once for the
			 * entire tree instead of once per row. Defaults to `false` when not provided.
			 */
			isSmartFoldersEnabledForTree?: boolean;
		} & RenderItemParams<ContentTreeItem>,
		'flags'
	>
> = withFlags(PageTreeItemComponent);

const i18n = defineMessages({
	checkboxLabel: {
		id: 'page-tree.page-tree-item.checkbox.label',
		defaultMessage: '{contentTitle} checkbox',
		description: 'Label for the checkbox to select a page tree item',
	},
	draftTooltip: {
		id: 'page-tree.page-tree-item.draft.tooltip',
		defaultMessage: 'Drafts can’t be selected for bulk actions.',
		description: 'Tooltip for disabled multiselect checkbox when item is a draft',
	},
	managedTooltip: {
		id: 'page-tree.page-tree-item.managed.tooltip',
		defaultMessage: 'Synced content can’t be selected for bulk actions.',
		description: 'Tooltip for disabled multiselect checkbox when item is synced content',
	},
});

const noop = () => {};
